import React from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';

const HowItWorksSection = styled.section`
  padding: ${theme.spacing[20]} ${theme.spacing[4]};
  background: linear-gradient(135deg, 
    #667eea 0%, 
    #764ba2 25%, 
    #f093fb 50%, 
    #f5576c 75%, 
    #4facfe 100%
  );
  overflow-x: hidden;
  width: 100%;
  scroll-margin-top: ${theme.navHeight.desktop};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(1px);
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[12]} ${theme.spacing[4]};
    scroll-margin-top: ${theme.navHeight.mobile};
  }
`;

const Container = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const MainTitle = styled.h2`
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24, #f0932b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  margin-bottom: ${theme.spacing[16]};
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing[12]};
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[12]};
  margin-bottom: ${theme.spacing[20]};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
    margin-bottom: ${theme.spacing[16]};
  }
`;

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  padding: ${theme.spacing[10]};
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24);
    border-radius: 30px 30px 0 0;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[8]};
  }
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[8]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.4),
    rgba(255, 255, 255, 0.6)
  );
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
  width: 100%;
  text-align: left;
  backdrop-filter: blur(5px);
  
  &:hover {
    transform: translate3d(0, -1px, 0);
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.7)
    );
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  &:active {
    transform: translate3d(0, 0, 0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SectionIcon = styled.div`
  font-size: 2.5rem;
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${theme.spacing[8]};
  font-style: italic;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
    margin-bottom: ${theme.spacing[6]};
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FeatureItem = styled.div`
  padding: ${theme.spacing[8]} ${theme.spacing[10]};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
  border-radius: 150% 40% 30% 70% / 60% 30% 70% 40%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  // Add subtle psychedelic glow
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(45deg, 
      rgba(102, 126, 234, 0.1),
      rgba(118, 75, 162, 0.1),
      rgba(240, 147, 251, 0.1),
      rgba(245, 87, 108, 0.1)
    );
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translate3d(0, -3px, 0) scale(1.02);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
    
    &::before {
      opacity: 1;
    }
  }
  
  // Give each blob a slightly different shape for variety
  &:nth-child(2) { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
  &:nth-child(3) { border-radius: 70% 30% 40% 60% / 30% 70% 60% 40%; }
  &:nth-child(4) { border-radius: 40% 70% 60% 30% / 70% 40% 50% 70%; }
  &:nth-child(5) { border-radius: 50% 50% 80% 20% / 40% 60% 40% 80%; }
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[6]} ${theme.spacing[8]};
  }
`;

const FeatureTitle = styled.h4`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.gray[800]};
  margin-bottom: ${theme.spacing[2]};
`;

const FeatureDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.gray[700]};
  line-height: 1.6;
`;

const Quote = styled.blockquote`
  margin-top: ${theme.spacing[8]};
  padding: ${theme.spacing[6]};
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  border-radius: 20px;
  border-left: 4px solid ${theme.colors.primary};
  font-style: italic;
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.gray[700]};
  text-align: center;
  position: relative;
  
  &::before {
    content: '"';
    font-size: 4rem;
    color: ${theme.colors.primary};
    position: absolute;
    top: -10px;
    left: 20px;
    font-family: serif;
  }
`;

const ConnectorSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 40px;
  padding: ${theme.spacing[12]};
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const ConnectorContent = styled.div`
  position: relative;
  z-index: 1;
`;

const ConnectorTitle = styled.h3`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${theme.spacing[8]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const ConnectorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
`;

const ConnectorItem = styled.div`
  padding: ${theme.spacing[4]};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const FinalQuote = styled.div`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.gray[800]};
  font-style: italic;
`;

const HowItWorks: React.FC = () => {
  return (
    <HowItWorksSection id="how-it-works">
      <Container>
        <MainTitle>🌿 How It Works</MainTitle>
        
        <MainGrid>
          <SectionCard>
            <SectionHeader onClick={() => {
              window.open('https://dashboard.greengrowers.club', '_self');
            }}>
              <SectionIcon>💻</SectionIcon>
              <div>
                <SectionTitle>For Growers</SectionTitle>
              </div>
            </SectionHeader>
            
            <SectionSubtitle>Manage. Showcase. Comply.</SectionSubtitle>
            
            <FeatureList>
              <FeatureItem>
                <FeatureTitle>Create your verified grower profile</FeatureTitle>
                <FeatureDescription>Add licensing info, photos, awards, and your cultivation story.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Track compliance</FeatureTitle>
                <FeatureDescription>Get alerts for OSHA requirements, export reports, and manage certifications.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Highlight sustainability</FeatureTitle>
                <FeatureDescription>Publish metrics on water use, energy, packaging, and eco-friendly practices.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Submit strains to our germplasm repository</FeatureTitle>
                <FeatureDescription>Add lab-tested data and win awards for your genetics.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Engage directly with conscious consumers</FeatureTitle>
                <FeatureDescription>Get rated, reviewed, and followed through the mobile app.</FeatureDescription>
              </FeatureItem>
            </FeatureList>
            
            <Quote>"Your story deserves a platform. <br /><span style={{ fontWeight: 'bold', color: theme.colors.primary }}>Green Growers Club</span> gives it roots."</Quote>
          </SectionCard>

          <SectionCard>
            <SectionHeader onClick={() => {
              window.open('https://greent.club', '_self');
            }}>
              <SectionIcon>📱</SectionIcon>
              <div>
                <SectionTitle>For Conscious Consumers</SectionTitle>
              </div>
            </SectionHeader>
            
            <SectionSubtitle>Discover. Learn. Support.</SectionSubtitle>
            
            <FeatureList>
              <FeatureItem>
                <FeatureTitle>Find verified, ethical growers near you</FeatureTitle>
                <FeatureDescription>Filter by certifications, sustainability badges, and reviews.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Smart Strain Search (S³)</FeatureTitle>
                <FeatureDescription>Get recommendations based on terpene preferences, effects, and genetics.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Review and rate strains</FeatureTitle>
                <FeatureDescription>Help the community and elevate growers who do it right.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Track your favorites</FeatureTitle>
                <FeatureDescription>Save strains, follow growers, and build your personal stash journal.</FeatureDescription>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureTitle>Shop consciously</FeatureTitle>
                <FeatureDescription>Learn what's in your flower before it hits your lungs.</FeatureDescription>
              </FeatureItem>
            </FeatureList>
            
            <Quote>"No more guessing. <br />Know your cannabis with <a href="https://greent.club" target="_self" rel="noopener noreferrer">Greent Club</a>."</Quote>
          </SectionCard>
        </MainGrid>

        <ConnectorSection>
          <ConnectorContent>
            <ConnectorTitle>
              🔗 Connected by Trust
            </ConnectorTitle>
            
            <ConnectorGrid>
              <ConnectorItem>
                <strong>Grower actions appear instantly in the app</strong>
              </ConnectorItem>
              <ConnectorItem>
                <strong>Consumer reviews feed into grower analytics</strong>
              </ConnectorItem>
              <ConnectorItem>
                <strong>Verified badges and lab results are automatically linked</strong>
              </ConnectorItem>
            </ConnectorGrid>
            
            <FinalQuote>
              Together, <span style={{ fontWeight: 'bold', color: theme.colors.primary }}>Green Growers Club's ecosystem</span> builds the Trust Layer for sustainable, transparent cannabis.
            </FinalQuote>
          </ConnectorContent>
        </ConnectorSection>
      </Container>
    </HowItWorksSection>
  );
};

export default HowItWorks; 